/**
 * Action-specific fields: tactic type, targets, schedule, and location
 * rows. The "Tasks for this action" container (toggle, collection name
 * input, task drafts list) lives in `ActionPostForm` directly so the
 * surrounding layout can keep tasks adjacent to the body editor.
 */
import React from "react";
import { MapPinned, Video, X, MapPinPlus } from "lucide-react";
import { DateTimePickerInput } from "ui/DateTimePicker";
import LocationSearchField, { LocationData } from "ui/LocationSearchField";
import { ToggleGroup, ToggleGroupItem } from "ui/ToggleGroup";
import InputField from "ui/InputField";
import InputTextArea from "ui/InputTextArea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "ui/InputSelect";
import AddButton from "ui/AddButton";
import { ACTION_TYPE_META, type ActionTypeValue } from "components/Posts/kindFields/actionTypeMeta";

export interface ActionTaskDraft {
  localId: string;
  title: string;
  dueAt: Date | null;
}

export interface ActionPlace {
  is_virtual: boolean;
  location: {
    action_url?: string;
    address?: string;
    latitude?: number;
    longitude?: number;
    place_name?: string;
  };
  note?: string;
}

function getFaviconForUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    return `https://icons.duckduckgo.com/ip2/${urlObj.hostname}.ico`;
  } catch {
    return `${process.env.REACT_APP_IMAGE_CDN}/icons/link_icon.svg`;
  }
}

export interface ActionFieldsProps {
  actionType: ActionTypeValue | "";
  onActionTypeChange: (v: ActionTypeValue) => void;
  targetsCount: number;
  onOpenTargets: () => void;
  startAt: Date | null;
  endAt: Date | null;
  onStartAtChange: (d: Date | null) => void;
  onEndAtChange: (d: Date | null) => void;
  userIsMobile: boolean;
  places: ActionPlace[];
  onPlaceFieldChange: (index: number, field: string, value: unknown) => void;
  onLocationTypeChange: (index: number, value: string) => void;
  onAddPlace: () => void;
  onRemovePlace: (index: number) => void;
}

const ActionFields: React.FC<ActionFieldsProps> = ({
  actionType,
  onActionTypeChange,
  targetsCount,
  onOpenTargets,
  startAt,
  endAt,
  onStartAtChange,
  onEndAtChange,
  userIsMobile,
  places,
  onPlaceFieldChange,
  onLocationTypeChange,
  onAddPlace,
  onRemovePlace,
}) => {
  return (
  <>
    <div className="form-control">
      <div className="flex flex-col gap-2">
        <div className="grid grid-cols-1 gap-2">
          <div className="form-control">
            <label className="block text-sm font-semibold" htmlFor="action-type-select">
              Type of action
            </label>
            <Select
              value={actionType || undefined}
              onValueChange={(val) => onActionTypeChange(val as ActionTypeValue)}
            >
              <SelectTrigger id="action-type-select" className="input-select px-3 py-2">
                <SelectValue placeholder="Select a type" />
              </SelectTrigger>
              <SelectContent className="border border-eoe-border-main dark:border-eoe-dark-border-main">
                {(Object.entries(ACTION_TYPE_META) as Array<[ActionTypeValue, { label: string; description: string }]>).map(
                  ([key, meta]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex flex-col text-left">
                        <span>{meta.label}</span>
                        <span className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub leading-tight">
                          {meta.description}
                        </span>
                      </div>
                    </SelectItem>
                  )
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="form-control">
            <span className="block text-sm font-semibold">Targets</span>
            <AddButton
              tooltipTitle={targetsCount > 0 ? "Update targeted issues" : "Add targeted issues"}
              buttonText={targetsCount > 0 ? "Update issues" : "Issues"}
              onClick={onOpenTargets}
              className="w-full"
            />
          </div>
        </div>
      </div>
    </div>

    <div className="flex flex-row justify-start gap-1">
      <div className="form-control">
        <DateTimePickerInput
          value={startAt}
          onChange={onStartAtChange}
          isMobile={userIsMobile}
          placeholder="Start time"
        />
      </div>
      <div className="form-control">
        <DateTimePickerInput value={endAt} onChange={onEndAtChange} isMobile={userIsMobile} placeholder="End time" />
      </div>
    </div>

    {/*
      Places (locations) follow the schedule so physical/virtual
      logistics stay grouped together. The task authoring block (task
      collection toggle + picker + drafts list) is rendered by
      `ActionPostForm` AFTER this section so tasks can sit next to the
      body editor.
    */}
    <div className="form-control">
      {places.map((place, index) => (
        <div
          key={index}
          className="mb-4 p-3 border border-eoe-border-main dark:border-eoe-dark-border-main rounded-md relative"
        >
          <div className="flex justify-between mb-4">
            <ToggleGroup
              type="single"
              value={place.is_virtual ? "online" : "offline"}
              className="space-x-0"
              onValueChange={(v) => v && onLocationTypeChange(index, v)}
            >
              <ToggleGroupItem value="online" className="flex items-center gap-1 px-3 rounded-l-lg rounded-r-none">
                <Video className="size-4" aria-hidden />
                <span>Online</span>
              </ToggleGroupItem>
              <ToggleGroupItem value="offline" className="flex items-center gap-1 px-3 rounded-l-none rounded-r-lg">
                <MapPinned className="size-4" aria-hidden />
                <span>Offline</span>
              </ToggleGroupItem>
            </ToggleGroup>
            <button
              type="button"
              onClick={() => onRemovePlace(index)}
              className="absolute top-2 right-2 p-1 text-eoe-text-sub dark:text-eoe-dark-text-sub hover:text-red-500 rounded-full"
              aria-label="Remove location"
            >
              <X className="size-4" />
            </button>
          </div>

          {place.is_virtual ? (
            <div className="mb-2">
              <div className="form-control mb-4">
                <InputField
                  type="url"
                  id={`action-url-${index}`}
                  name={`action-url-${index}`}
                  labelText="URL"
                  fieldPlaceholder="https://zoom.us/j/123456789"
                  value={place.location.action_url || ""}
                  onChange={(e) => onPlaceFieldChange(index, "action_url", e.target.value)}
                  fieldRequired
                  className="w-full"
                  spellCheck={false}
                />
                {place.location.action_url ? (
                  <div className="flex items-center gap-2 mt-2">
                    <img
                      className="size-4 rounded-sm"
                      src={getFaviconForUrl(place.location.action_url)}
                      alt=""
                      onError={(e) => {
                        const target = e.currentTarget;
                        target.onerror = null;
                        try {
                          const u = new URL(place.location.action_url || "");
                          const hostname = u.hostname;
                          const services = [
                            `https://www.google.com/s2/favicons?domain_url=${hostname}`,
                            `https://favicon.yandex.net/favicon/${hostname}`,
                            `https://f1.allesedv.com/16/${hostname}`,
                            `${process.env.REACT_APP_IMAGE_CDN}/icons/link_icon.svg`,
                          ];
                          let currentIndex = 0;
                          const tryNextService = () => {
                            if (currentIndex < services.length) {
                              target.src = services[currentIndex];
                              currentIndex += 1;
                              if (currentIndex < services.length) {
                                target.onerror = tryNextService;
                              }
                            }
                          };
                          tryNextService();
                        } catch {
                          target.src = `${process.env.REACT_APP_IMAGE_CDN}/icons/link_icon.svg`;
                        }
                      }}
                    />
                    <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                      {place.location.action_url}
                    </span>
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="mb-2">
              <LocationSearchField
                fieldPlaceholder="Search for any address or place"
                className="w-full"
                onChange={(loc: LocationData | null) => {
                  if (loc) onPlaceFieldChange(index, "location_data", loc);
                }}
                value={
                  place.location?.address
                    ? {
                        formatted_address: place.location.address,
                        city: "",
                        state: "",
                        country: "",
                        lat: place.location.latitude || 0,
                        lng: place.location.longitude || 0,
                        placeId: "",
                      }
                    : null
                }
              />
            </div>
          )}

          <div className="mt-4">
            <InputTextArea
              id={`place-notes-${index}`}
              name="Notes"
              labelText="Notes"
              value={place.note || ""}
              onChange={(e) => onPlaceFieldChange(index, "note", e.target.value)}
              fieldPlaceholder="Additional information about the location"
              rows={2}
            />
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={onAddPlace}
        className="btn-secondary bg-eoe-icon dark:bg-eoe-dark-icon hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub flex items-center gap-1"
      >
        <MapPinPlus className="size-4" aria-hidden />
        {places.length === 0 ? "Add a location" : "Add another location"}
      </button>
    </div>
  </>
  );
};

export default ActionFields;
